import { format } from "date-fns";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { EmptyState, ErrorState, PageLoading } from "../../components/ui/loading";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  type CommsAudience,
  type CommsAudienceFilter,
  useAudiences,
  useCreateAudience,
  useDeleteAudience,
  useUpdateAudience,
} from "../../hooks/use-comms";

interface AudienceFormValues {
  name: string;
  description?: string;
  filterJson: CommsAudienceFilter;
}

function AudienceForm({
  initial,
  onSave,
  onCancel,
  isSaving,
}: {
  initial: Partial<CommsAudience>;
  onSave: (input: AudienceFormValues) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const [name, setName] = useState(initial.name ?? "");
  const [description, setDescription] = useState(initial.description ?? "");
  const [status, setStatus] = useState<"any" | "active" | "cancelled">(
    initial.filterJson?.status === "active" || initial.filterJson?.status === "cancelled"
      ? initial.filterJson.status
      : "any",
  );

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="a-name" className="text-xs font-medium text-muted-foreground block mb-1">
          Audience Name
        </label>
        <input
          id="a-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="audience-name"
        />
      </div>
      <div>
        <label htmlFor="a-desc" className="text-xs font-medium text-muted-foreground block mb-1">
          Description
        </label>
        <input
          id="a-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="audience-description"
        />
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">Filter Criteria</p>
        <div>
          <label htmlFor="a-status" className="text-xs text-muted-foreground block mb-1">
            Insurance Record Status
          </label>
          <select
            id="a-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as "any" | "active" | "cancelled")}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"
            data-testid="audience-status"
          >
            <option value="any">Any</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button
          onClick={() =>
            onSave({
              name,
              description: description || undefined,
              filterJson: { source: "insurance", status: status === "any" ? "all" : status },
            })
          }
          disabled={!name.trim() || isSaving}
          data-testid="audience-save"
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function AudiencesPage(): React.ReactElement {
  const { data, isLoading, error, refetch } = useAudiences();
  const createAudience = useCreateAudience();
  const [editing, setEditing] = useState<CommsAudience | null>(null);
  const updateAudience = useUpdateAudience(editing?.id ?? "");
  const deleteAudience = useDeleteAudience();
  const [showForm, setShowForm] = useState(false);

  if (isLoading) return <PageLoading />;
  if (error) return <ErrorState message="Failed to load audiences." onRetry={() => refetch()} />;

  const audiences = data?.items ?? [];

  function openCreate(): void {
    setEditing(null);
    setShowForm(true);
  }

  function openEdit(audience: CommsAudience): void {
    setEditing(audience);
    setShowForm(true);
  }

  function handleSave(input: AudienceFormValues): void {
    if (editing) {
      updateAudience.mutate(input, { onSuccess: () => setShowForm(false) });
    } else {
      createAudience.mutate(input, { onSuccess: () => setShowForm(false) });
    }
  }

  function handleDelete(id: string): void {
    if (!window.confirm("Delete this audience? Campaigns that used it will keep their send history.")) return;
    deleteAudience.mutate(id);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{audiences.length} audiences configured</p>
        <Button size="sm" onClick={openCreate} data-testid="create-audience">
          <Plus className="h-3.5 w-3.5" /> Create New
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right w-24">Records</TableHead>
            <TableHead className="w-28">Created</TableHead>
            <TableHead className="w-28">Modified</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {audiences.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <EmptyState title="No audiences" description="No audiences created yet." />
              </TableCell>
            </TableRow>
          ) : (
            audiences.map((a) => (
              <TableRow key={a.id} data-testid={`audience-row-${a.id}`}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-muted-foreground">{a.description}</TableCell>
                <TableCell className="text-right tabular-nums">{a.recordCount}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(a.createdAt), "dd MMM yyyy")}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(a.updatedAt), "dd MMM yyyy")}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`audience-edit-${a.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(a.id)}
                      data-testid={`audience-delete-${a.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Target Audience" : "Create Target Audience"}</DialogTitle>
          </DialogHeader>
          <AudienceForm
            initial={editing ?? {}}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            isSaving={createAudience.isPending || updateAudience.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
